import React, { useEffect, useState } from "react";
import styled from "styled-components";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #7e7e7e;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: #f0f0f0;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  max-height: calc(90vh - 100px);
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

export default function NewCampaignModal({ isOpen, onClose, onSubmit, loading, templates = [] }) {
  const [form, setForm] = useState({ name: "", description: "", start_date: "", end_date: "", debrief_report_id: "" });

  useEffect(() => {
    if (!isOpen) {
      setForm({ name: "", description: "", start_date: "", end_date: "", debrief_report_id: "" });
    }
  }, [isOpen]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form });
  };

  // Check if all required fields are filled
  const isFormValid = form.name.trim() !== "" &&
    form.start_date !== "" &&
    form.end_date !== "" &&
    form.debrief_report_id !== "";

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>New Campaign</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Name *</FormLabel>
              <FormInput
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="e.g. Q4 Engagement Campaign"
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Description</FormLabel>
              <FormTextarea
                name="description"
                value={form.description}
                onChange={onChange}
                placeholder="Describe the campaign"
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <FormLabel>Start Date *</FormLabel>
                  <FormInput
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={onChange}
                    disabled={loading}
                  />
                </div>
                <div>
                  <FormLabel>End Date *</FormLabel>
                  <FormInput
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={onChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <FormLabel>Report Template *</FormLabel>
              <FormSelect
                name="debrief_report_id"
                value={form.debrief_report_id}
                onChange={onChange}
                disabled={loading}
              >
                <option value="" disabled>Select a report template</option>
                {Array.isArray(templates) && templates.map(t => (
                  <option key={t.id} value={t.id}>{t.report_name || t.tracking_code || `Report ${t.id}`}</option>
                ))}
              </FormSelect>
            </FormGroup>

            <ModalActions>
              <CancelButton type="button" onClick={onClose} disabled={loading}>
                Cancel
              </CancelButton>
              <SaveButton type="submit" disabled={loading || !isFormValid}>
                {loading ? "Creating..." : "Create"}
              </SaveButton>
            </ModalActions>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}
